<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="cashreport-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Raport kasowy</h1>
      </div>
    </div>
    <v-card class="mb-3">
      <v-card-title>
        Raport kasowy
        <v-spacer></v-spacer>
            <v-btn small outlined color="green" class="mr-2" @click="downloadExcel" :loading="loadingExcel">
              <v-icon left small>mdi-file-excel</v-icon> Excel
            </v-btn>
            <v-btn small outlined color="teal" class="mr-2" @click="downloadCsv" :loading="loadingCsv">
              <v-icon left small>mdi-file-delimited</v-icon> CSV
            </v-btn>
            <v-btn small outlined color="red" @click="downloadPdf" :loading="loadingPdf">
              <v-icon left small>mdi-file-pdf-box</v-icon> PDF
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="3">
                <v-select v-model.number="month" :items="monthOptions" :label="$t('common.month')" outlined dense />
              </v-col>
              <v-col cols="12" md="3">
                <v-select v-model.number="year" :items="yearOptions" :label="$t('common.year')" outlined dense />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="subCompanyId"
                  :items="subCompanyOptions"
                  item-text="name"
                  item-value="id"
                  label="Oddział"
                  outlined dense
                />
              </v-col>
              <v-col cols="12" md="3" class="d-flex align-center">
                <v-btn color="primary" @click="openEntryDialog()">
                  <v-icon left>mdi-plus</v-icon> {{ $t('common.addEntry') }}
                </v-btn>
              </v-col>
            </v-row>

            <!-- Stan kasy na pierwszym planie. Cztery jednakowe kafelki myliły:
                 saldo początkowe (przeniesione z poprzedniego miesiąca) bywało
                 czytane jako stan gotówki i wyglądało, jakby „nie malało" mimo
                 wypłat — zgłoszenie Anny Garasiewicz 02.09.2026. -->
            <v-row class="mt-2 align-stretch">
              <v-col cols="12" md="5">
                <div class="kasa-stan">
                  <div class="kasa-stan__label">Stan kasy teraz</div>
                  <div class="kasa-stan__kwota">{{ fmt(totals.closingBalance) }}</div>
                  <div class="kasa-stan__opis">
                    Tyle powinno być w kasetce po wszystkich wpisach z tego miesiąca.
                  </div>
                  <!-- „Kasetka przeliczona” — zgłoszenie Ani 20.10.2026. -->
                  <div class="kasa-przeliczenie">
                    <div v-if="ostatniePrzeliczenie" class="kasa-przeliczenie__ostatnie">
                      <v-icon small class="mr-1">mdi-cash-check</v-icon>
                      Przeliczona {{ fmtDateTime(ostatniePrzeliczenie.countedAt) }} ·
                      {{ ostatniePrzeliczenie.countedByName }} ·
                      <span :class="ostatniePrzeliczenie.difference ? 'kasa-przeliczenie--roznica' : 'kasa-przeliczenie--ok'">
                        {{ ostatniePrzeliczenie.difference
                          ? 'różnica ' + (ostatniePrzeliczenie.difference > 0 ? '+' : '') + fmt(ostatniePrzeliczenie.difference)
                          : 'zgodna' }}
                      </span>
                    </div>
                    <div v-else class="kasa-przeliczenie__ostatnie">Kasetka nieprzeliczana w tym miesiącu.</div>
                    <v-btn v-if="biezacyMiesiac" small outlined class="mt-2" @click="otworzPrzeliczenie">
                      <v-icon left small>mdi-cash-check</v-icon>Kasetka przeliczona
                    </v-btn>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="7">
                <div class="kasa-rozbicie">
                  <div class="kasa-rozbicie__pozycja">
                    <span>Z poprzedniego miesiąca</span>
                    <strong>{{ fmt(openingBalance) }}</strong>
                  </div>
                  <div class="kasa-rozbicie__pozycja kasa-rozbicie__pozycja--plus">
                    <span>Wpłaty</span>
                    <strong>+{{ fmt(totals.deposits) }}</strong>
                  </div>
                  <div class="kasa-rozbicie__pozycja kasa-rozbicie__pozycja--minus">
                    <span>Wypłaty</span>
                    <strong>−{{ fmt(totals.withdrawals) }}</strong>
                  </div>
                  <div v-if="totals.corrections" class="kasa-rozbicie__pozycja kasa-rozbicie__pozycja--minus">
                    <span>Korekty</span>
                    <strong>−{{ fmt(totals.corrections) }}</strong>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-data-table
          :headers="headers"
          :items="entries"
          sort-by="date"
          sort-desc
          class="elevation-1"
          :loading="loading"
          :items-per-page="50"
          :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
        >
          <template v-slot:item.date="{ item }">{{ fmtDate(item.date) }}</template>
          <template v-slot:item.type="{ item }">
            <v-chip small :color="typeColor(item.type)" text-color="white">
              {{ typeLabel(item.type) }}
            </v-chip>
          </template>
          <template v-slot:item.amount="{ item }">{{ fmt(item.amount) }}</template>
          <template v-slot:item.recipient="{ item }">
            <span v-if="item.driver">{{ item.driver.firstName }} {{ item.driver.lastName }}</span>
            <span v-else>{{ item.recipient || '—' }}</span>
          </template>
          <template v-slot:item.receipt="{ item }">
            <template v-if="item.receiptRequired">
              <v-icon
                v-if="item.hasSignature && item.hasWorkerSignature"
                small color="success" title="Pokwitowane — oba podpisy"
              >mdi-check-decagram</v-icon>
              <v-icon
                v-else-if="item.hasSignature || item.hasWorkerSignature"
                small color="warning" title="Pokwitowanie podpisane tylko przez jedną stronę"
              >mdi-alert-decagram-outline</v-icon>
              <v-icon
                v-else small color="error" title="Wymaga pokwitowania — brak podpisów"
              >mdi-signature-freehand</v-icon>
            </template>
            <span v-else class="grey--text">—</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn x-small color="info" class="mr-1" @click="downloadReceipt(item)" title="Pokwitowanie">
              <v-icon x-small>mdi-receipt</v-icon>
            </v-btn>
            <v-btn x-small color="warning" class="mr-1" @click="openEntryDialog(item)" title="Edytuj">
              <v-icon x-small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn x-small color="error" @click="deleteEntry(item)" title="Usuń">
              <v-icon x-small>mdi-delete</v-icon>
            </v-btn>
          </template>
    </v-data-table>

    <v-dialog content-class="dark-form-dialog" v-model="przeliczenie.open" max-width="460px">
      <v-card>
        <v-card-title>Kasetka przeliczona</v-card-title>
        <v-card-text>
          <p class="mb-3">Stan w systemie: <strong>{{ fmt(totals.closingBalance) }}</strong></p>
          <v-text-field
            v-model="przeliczenie.kwota"
            type="number"
            step="0.01"
            min="0"
            label="Ile jest w kasetce"
            suffix="zł"
            outlined
            dense
            autofocus
          />
          <div v-if="roznicaPrzeliczenia" class="kasa-przeliczenie--roznica mb-2">
            Różnica: {{ roznicaPrzeliczenia > 0 ? '+' : '' }}{{ fmt(roznicaPrzeliczenia) }}
            ({{ roznicaPrzeliczenia > 0 ? 'nadwyżka' : 'brak' }})
          </div>
          <v-textarea v-model="przeliczenie.uwagi" label="Uwagi (opcjonalnie)" outlined dense rows="2" />
          <div class="text-caption grey--text">Zapisze się data, godzina i kto przeliczył.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="przeliczenie.open = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="przeliczenie.zapisywanie" :disabled="przeliczenie.kwota === '' || przeliczenie.kwota === null" @click="zapiszPrzeliczenie">Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog content-class="dark-form-dialog" v-model="dialog" max-width="600px">
      <v-card>
        <v-card-title>{{ currentEntryId ? 'Edytuj wpis' : 'Nowy wpis' }}</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12" md="6">
                <v-menu v-model="dateMenu" :close-on-content-click="false" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="entry.date"
                      label="Data"
                      outlined
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :rules="[required]"
                    />
                  </template>
                  <v-date-picker
                    v-model="entry.date"
                    no-title
                    @input="dateMenu = false"
                    first-day-of-week="1"
                    locale="pl"
                  />
                </v-menu>
              </v-col>
              <v-col cols="12" md="6">
                <v-select v-model="entry.type" :items="typeOptions" label="Typ" outlined :rules="[required]" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model.number="entry.amountZl" type="number" label="Kwota (zł)" outlined :rules="[required, positive]" />
              </v-col>
              <v-col cols="12" md="8">
                <v-combobox
                  v-model="entry.odbiorca"
                  :items="cashDrivers"
                  item-text="label"
                  return-object
                  label="Dla kogo / kierowca"
                  outlined
                  clearable
                  hide-no-data
                  hint="Wybierz kierowcę z listy albo wpisz z ręki — np. warsztat, poczta, urząd"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="entry.operator" label="Kto obsłużył (inicjały)" outlined />
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="entry.description" label="Za co / opis" outlined rows="2" :rules="[required]" />
              </v-col>

              <v-col cols="12" class="pt-0">
                <v-checkbox
                  v-model="entry.receiptRequired"
                  label="Pokwitowanie z podpisem"
                  hint="Zaznacz, gdy operacja ma być pokwitowana — np. wypłata gotówki kierowcy. Zakup do biura tego nie wymaga."
                  persistent-hint
                  class="mt-0"
                />
              </v-col>

              <template v-if="entry.receiptRequired">
                <v-col cols="12">
                  <v-alert dense text type="info" class="mb-0">
                    <div class="d-flex align-center flex-wrap" style="gap:8px">
                      <v-icon small color="info">mdi-tablet-dashboard</v-icon>
                      <span class="subtitle-2">Podpis na tablecie</span>
                      <v-spacer />
                      <template v-if="signatureDevices.length">
                        <v-btn
                          small color="info" :loading="tabletWaiting"
                          :disabled="!tabletKwotaOk"
                          @click="requestTabletSignature()"
                        >
                          <v-icon left small>mdi-send</v-icon>
                          {{ tabletWaiting ? 'Czekam na podpis…' : 'Poproś o podpis na tablecie' }}
                        </v-btn>
                        <v-btn v-if="tabletWaiting" small text @click="cancelTabletSignature()">Anuluj</v-btn>
                      </template>
                      <v-btn v-else small text color="info" @click="startTabletPairing()">
                        <v-icon left small>mdi-plus</v-icon>Sparuj tablet
                      </v-btn>
                    </div>
                    <div v-if="!tabletKwotaOk" class="caption mt-1">
                      Wpisz kwotę i odbiorcę — tablet pokaże je osobie podpisującej.
                    </div>
                    <div v-if="tabletWaiting" class="caption mt-1">
                      Podaj tablet {{ entry.type === 'DEPOSIT' ? 'wpłacającemu' : 'odbierającemu' }} —
                      podpis pojawi się tu automatycznie.
                    </div>
                    <div v-if="tabletSignature" class="caption success--text mt-1">
                      <v-icon x-small color="success">mdi-check-circle</v-icon>
                      Podpis odebrany z tabletu — zostanie zapisany razem z wpisem.
                      <v-btn x-small text @click="tabletSignature = null">Cofnij</v-btn>
                    </div>
                    <div v-if="tabletError" class="caption error--text mt-1">{{ tabletError }}</div>
                    <div v-if="tabletPairing" class="mt-2">
                      <div class="caption">Wpisz ten kod na tablecie (strona <b>/podpis</b>):</div>
                      <div class="text-h4 font-weight-bold" style="letter-spacing:10px">{{ tabletPairing }}</div>
                      <div class="caption">Kod jest jednorazowy i wygasa po 10 minutach.</div>
                    </div>
                  </v-alert>
                </v-col>

                <v-col cols="12" md="6">
                  <div class="d-flex align-center mb-1">
                    <v-icon small left color="primary">mdi-signature-freehand</v-icon>
                    <span class="body-2 font-weight-medium">
                      {{ entry.type === 'DEPOSIT' ? 'Podpis wpłacającego' : 'Podpis odbierającego' }}
                    </span>
                    <v-spacer />
                    <v-btn x-small text @click="clearSignature('receiver')">
                      <v-icon x-small left>mdi-eraser</v-icon>Wyczyść
                    </v-btn>
                  </div>
                  <div v-if="tabletSignature" class="signature-pad d-flex align-center justify-center">
                    <img :src="tabletSignature" alt="Podpis z tabletu" style="max-height:150px;max-width:100%" />
                  </div>
                  <SignatureField
                    v-else
                    width="100%"
                    height="160px"
                    ref="receiverSignaturePad"
                    class="signature-pad"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <div class="d-flex align-center mb-1">
                    <v-icon small left color="secondary">mdi-signature-freehand</v-icon>
                    <span class="body-2 font-weight-medium">Podpis wydającego z kasy</span>
                    <v-spacer />
                    <v-btn x-small text @click="clearSignature('worker')">
                      <v-icon x-small left>mdi-eraser</v-icon>Wyczyść
                    </v-btn>
                  </div>
                  <SignatureField
                    width="100%"
                    height="160px"
                    ref="workerSignaturePad"
                    class="signature-pad"
                  />
                </v-col>
                <v-col v-if="currentEntryId && (entrySigned.receiver || entrySigned.worker)" cols="12" class="pt-0">
                  <v-alert dense text type="info" class="mb-0 body-2">
                    Ten wpis ma już złożone podpisy
                    ({{ [entrySigned.receiver ? 'odbierający' : null, entrySigned.worker ? 'wydający' : null].filter(Boolean).join(', ') }}).
                    Zostaną zachowane, chyba że podpiszesz na nowo.
                  </v-alert>
                </v-col>
              </template>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="secondary" @click="dialog = false">Anuluj</v-btn>
          <v-btn color="primary" :disabled="!valid" @click="saveEntry">Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import SignatureField from "@/components/SignatureField.vue";
import Swal from "sweetalert2";
const axios = require("axios");
import * as dayjs from "dayjs";

export default {
  components: { SignatureField },
  data() {
    const now = new Date();
    return {
      loading: false,
      loadingExcel: false,
      loadingPdf: false,
      loadingCsv: false,
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      subCompanyId: null,
      subCompanyOptions: [],
      openingBalance: 0,
      entries: [],
      totals: { deposits: 0, withdrawals: 0, corrections: 0, closingBalance: 0 },
      counts: [],
      przeliczenie: { open: false, kwota: '', uwagi: '', zapisywanie: false },
      cashDrivers: [],
      dialog: false,
      dateMenu: false,
      valid: false,
      currentEntryId: null,
      entry: this.emptyEntry(),
      // Czy edytowany wpis ma już złożone podpisy (same obrazki nie schodzą
      // z serwera — w liście są tylko flagi).
      entrySigned: { receiver: false, worker: false },
      // Podpis na tablecie — ten sam mechanizm co przy wypłatach gotówkowych
      // (SignatureRequest). Tablet pokazuje kwotę i odbiorcę, podpis wraca tu
      // i zapisuje się jako podpis odbierającego/wpłacającego.
      signatureDevices: [],
      tabletWaiting: false,
      tabletError: '',
      tabletPairing: null,
      tabletRequestId: null,
      tabletPoll: null,
      tabletSignature: null,
      monthOptions: [
        { text: 'Styczeń', value: 1 },
        { text: 'Luty', value: 2 },
        { text: 'Marzec', value: 3 },
        { text: 'Kwiecień', value: 4 },
        { text: 'Maj', value: 5 },
        { text: 'Czerwiec', value: 6 },
        { text: 'Lipiec', value: 7 },
        { text: 'Sierpień', value: 8 },
        { text: 'Wrzesień', value: 9 },
        { text: 'Październik', value: 10 },
        { text: 'Listopad', value: 11 },
        { text: 'Grudzień', value: 12 },
      ],
      typeOptions: [
        { text: 'Wpłata', value: 'DEPOSIT' },
        { text: 'Wypłata', value: 'WITHDRAWAL' },
        { text: 'Korekta', value: 'CORRECTION' },
      ],
      headers: [
        { text: 'Data', value: 'date', sortable: true },
        { text: 'Typ', value: 'type', sortable: true },
        { text: 'Kwota', value: 'amount', sortable: true },
        { text: 'Za co', value: 'description', sortable: false },
        { text: 'Dla kogo / kierowca', value: 'recipient', sortable: false },
        { text: 'Kto wykonał', value: 'kto', sortable: true },
        { text: 'Pokwitowanie', value: 'receipt', sortable: false, align: 'center' },
        { text: 'Akcje', value: 'actions', sortable: false, width: 160 },
      ],
    };
  },
  computed: {
    ostatniePrzeliczenie() {
      return this.counts[0] || null;
    },
    // Przeliczenie zawsze dotyczy dzisiejszej kasetki, więc tylko w bieżącym miesiącu.
    biezacyMiesiac() {
      const teraz = dayjs();
      return this.year === teraz.year() && this.month === teraz.month() + 1;
    },
    roznicaPrzeliczenia() {
      if (this.przeliczenie.kwota === '' || this.przeliczenie.kwota === null) return 0;
      return Math.round(Number(this.przeliczenie.kwota) * 100) - (this.totals.closingBalance || 0);
    },
    yearOptions() {
      const y = new Date().getFullYear();
      return [y - 2, y - 1, y, y + 1];
    },
    // Tablet ma pokazać konkretną kwotę i komu — bez tego prośba nie ma sensu.
    tabletKwotaOk() {
      const kwota = Number(this.entry.amountZl) || 0;
      return kwota > 0 && !!this.nazwaPodpisujacego;
    },
    // Pole „Dla kogo / kierowca" trzyma obiekt, gdy ktoś wybrał kierowcę z listy,
    // albo goły tekst, gdy wpisał odbiorcę z ręki (warsztat, poczta, urząd).
    odbiorcaId() {
      const v = this.entry.odbiorca;
      return v && typeof v === 'object' ? v.id : null;
    },
    odbiorcaNazwa() {
      const v = this.entry.odbiorca;
      if (!v) return '';
      return typeof v === 'object' ? v.label || '' : String(v).trim();
    },
    nazwaPodpisujacego() {
      return this.odbiorcaNazwa;
    },
  },
  async created() {
    await Promise.all([
      this.fetchSubCompanies(), this.fetchReport(),
      this.fetchCashDrivers(), this.loadSignatureDevices(),
    ]);
  },
  beforeDestroy() {
    // Odpytywanie tabletu nie może zostać po wyjściu ze strony.
    this.stopTabletWaiting();
  },
  watch: {
    year() { this.fetchReport(); },
    month() { this.fetchReport(); },
    subCompanyId() { this.fetchReport(); },
  },
  methods: {
    emptyEntry() {
      return {
        date: dayjs().format('YYYY-MM-DD'),
        type: 'WITHDRAWAL',
        amountZl: null,
        description: '',
        odbiorca: null,
        operator: null,
        receiptRequired: false,
      };
    },
    /** Sparowane tablety firmy — te z samym kodem jeszcze nie liczą się. */
    async loadSignatureDevices() {
      try {
        const r = await axios.get(process.env.VUE_APP_ROOT_API + 'signature/devices', {
          headers: this.authHeaders(),
        });
        // Sortujemy od ostatnio widzianego. Panel bierze z tej listy pierwszy
        // element, a wczesniej kolejnosc szla od NAJSTARSZEGO wpisu — wiec po
        // przeparowaniu tabletu (nowy rekord, ta sama nazwa) zadania lecialy na
        // martwy wpis i okno podpisu nigdy sie nie otwieralo. 30.09.2026
        // zablokowalo to podpisy na tydzien. Backend sortuje tak samo; tu
        // powtarzamy to u siebie, zeby nie zalezec od kolejnosci z serwera.
        const wgOstatnioWidzianego = (a, b) =>
          new Date(b.lastSeenAt || 0) - new Date(a.lastSeenAt || 0);
        this.signatureDevices = (r.data.data || [])
          .filter((d) => d.pairedAt)
          .sort(wgOstatnioWidzianego);
      } catch (e) {
        this.signatureDevices = [];
      }
    },
    async startTabletPairing() {
      this.tabletError = '';
      try {
        const r = await axios.post(
          process.env.VUE_APP_ROOT_API + 'signature/devices/pair-code',
          { name: 'Tablet do podpisu' },
          { headers: this.authHeaders() },
        );
        this.tabletPairing = r.data.data.pairingCode;
      } catch (e) {
        this.tabletError = e.response?.data?.message || 'Nie udało się wygenerować kodu';
      }
    },
    /** Wysyła na tablet kwotę pokwitowania i czeka na podpis. */
    /**
     * Tablet, na ktory wysylamy prosbe o podpis: ten, ktory odezwal sie
     * NAJPOZNIEJ. Lista jest juz tak posortowana, ale wybor nazywamy wprost,
     * bo wczesniej stalo tu `signatureDevices[0]` i przy dwoch wpisach o tej
     * samej nazwie (po przeparowaniu) trafialo to w martwy.
     */
    tabletDoPodpisu() {
      return (this.signatureDevices || [])
        .slice()
        .sort((a, b) => new Date(b.lastSeenAt || 0) - new Date(a.lastSeenAt || 0))[0];
    },
    async requestTabletSignature() {
      this.tabletError = '';
      const grosze = Math.round((Number(this.entry.amountZl) || 0) * 100);
      try {
        const r = await axios.post(
          process.env.VUE_APP_ROOT_API + 'signature/requests',
          {
            deviceId: this.tabletDoPodpisu().id,
            driverName: this.nazwaPodpisujacego,
            totalCents: grosze,
            items: [{ date: this.entry.date, amount: grosze }],
          },
          { headers: this.authHeaders() },
        );
        this.tabletRequestId = r.data.data.id;
        this.tabletWaiting = true;
        this.pollTabletSignature();
      } catch (e) {
        this.tabletError = e.response?.data?.message || 'Nie udało się wysłać prośby na tablet';
      }
    },
    pollTabletSignature() {
      clearInterval(this.tabletPoll);
      this.tabletPoll = setInterval(async () => {
        if (!this.tabletRequestId) return;
        try {
          const r = await axios.get(
            process.env.VUE_APP_ROOT_API + 'signature/requests/' + this.tabletRequestId,
            { headers: this.authHeaders() },
          );
          const d = r.data.data;
          if (d.status === 'SIGNED' && d.signature) {
            // Podpis z tabletu wchodzi tak samo jak złożony na ekranie —
            // zapis wpisu nie musi wiedzieć, skąd pochodzi.
            this.tabletSignature = d.signature;
            this.stopTabletWaiting();
          } else if (d.status === 'EXPIRED' || d.status === 'CANCELLED') {
            this.tabletError = 'Prośba wygasła — poproś o podpis ponownie';
            this.stopTabletWaiting();
          }
        } catch (e) {
          this.tabletError = 'Utracono połączenie z tabletem';
          this.stopTabletWaiting();
        }
      }, 2000);
    },
    stopTabletWaiting() {
      clearInterval(this.tabletPoll);
      this.tabletPoll = null;
      this.tabletWaiting = false;
      this.tabletRequestId = null;
    },
    async cancelTabletSignature() {
      const id = this.tabletRequestId;
      this.stopTabletWaiting();
      if (!id) return;
      try {
        await axios.post(
          process.env.VUE_APP_ROOT_API + 'signature/requests/' + id + '/cancel',
          {},
          { headers: this.authHeaders() },
        );
      } catch (e) { /* anulowanie nie może blokować zapisu wpisu */ }
    },
    /** Czyści wskazany podpis w oknie wpisu. */
    clearSignature(which) {
      const pad = this.$refs[which === 'worker' ? 'workerSignaturePad' : 'receiverSignaturePad'];
      if (pad && pad.clearSignature) pad.clearSignature();
    },
    /** Zdejmuje podpis z pola — zwraca base64 albo null, gdy nie podpisano. */
    grabSignature(which) {
      const pad = this.$refs[which === 'worker' ? 'workerSignaturePad' : 'receiverSignaturePad'];
      return pad ? pad.grab() : null;
    },
    typeLabel(type) {
      return { DEPOSIT: 'Wpłata', WITHDRAWAL: 'Wypłata', CORRECTION: 'Korekta' }[type] || type;
    },
    typeColor(type) {
      return { DEPOSIT: 'success', WITHDRAWAL: 'error', CORRECTION: 'warning' }[type] || 'grey';
    },
    required(v) { return (v !== null && v !== undefined && v !== '') || 'Wymagane'; },
    positive(v) { return (v > 0) || 'Musi być > 0'; },
    fmt(grosze) {
      return ((grosze || 0) / 100).toFixed(2) + ' zł';
    },
    fmtDate(d) {
      return dayjs(d).format('DD-MM-YYYY');
    },
    fmtDateTime(d) {
      return dayjs(d).format('DD-MM-YYYY HH:mm');
    },
    otworzPrzeliczenie() {
      this.przeliczenie = {
        open: true,
        kwota: ((this.totals.closingBalance || 0) / 100).toFixed(2),
        uwagi: '',
        zapisywanie: false,
      };
    },
    async zapiszPrzeliczenie() {
      this.przeliczenie.zapisywanie = true;
      try {
        const r = await axios.post(
          process.env.VUE_APP_ROOT_API + 'cash-report/counts',
          { countedAmount: Number(this.przeliczenie.kwota), note: this.przeliczenie.uwagi },
          { params: { subCompanyId: this.subCompanyId || '' }, headers: this.authHeaders() },
        );
        this.przeliczenie.open = false;
        await this.fetchReport();
        const roznica = r.data.data.countedAmount - r.data.data.expectedAmount;
        Swal.fire(
          roznica ? 'Zapisano — jest różnica' : 'Zapisano',
          roznica
            ? `Kasetka: ${this.fmt(r.data.data.countedAmount)}, system: ${this.fmt(r.data.data.expectedAmount)}.`
            : 'Kasetka zgodna ze stanem w systemie.',
          roznica ? 'warning' : 'success',
        );
      } catch (e) {
        Swal.fire('Błąd', (e.response && e.response.data && e.response.data.message) || 'Nie udało się zapisać przeliczenia', 'error');
      }
      this.przeliczenie.zapisywanie = false;
    },
    authHeaders() {
      return { Authorization: "Bearer " + localStorage.getItem("token") };
    },
    async fetchReport() {
      this.loading = true;
      try {
        const r = await axios.get(process.env.VUE_APP_ROOT_API + 'cash-report', {
          params: { year: this.year, month: this.month, subCompanyId: this.subCompanyId || '' },
          headers: this.authHeaders(),
        });
        const d = r.data.data;
        this.openingBalance = d.openingBalance;
        // „Kto wykonał": inicjały operatora z papierowej kasy, a jeśli ich nie
        // ma — admin, który wprowadził wpis w panelu.
        this.entries = (d.entries || []).map((e) => ({
          ...e,
          kto: e.operator || e.createdByName || '—',
        }));
        this.totals = d.totals;
        this.counts = d.counts || [];
      } catch (e) {
        console.log(e);
        Swal.fire('Błąd', 'Nie udało się pobrać raportu', 'error');
      }
      this.loading = false;
    },
    async fetchSubCompanies() {
      try {
        const r = await axios.get(process.env.VUE_APP_ROOT_API + 'cash-report/sub-companies', {
          headers: this.authHeaders(),
        });
        this.subCompanyOptions = r.data.data || [];
        // Brak opcji „bez oddziału" — domyślnie wybrany oddział Bydgoszcz
        // (fallback: pierwszy dostępny). Watcher subCompanyId odświeży raport.
        if (!this.subCompanyId && this.subCompanyOptions.length) {
          const byd = this.subCompanyOptions.find(
            (s) => (s.name || '').toLowerCase().includes('bydgoszcz'),
          );
          this.subCompanyId = byd ? byd.id : this.subCompanyOptions[0].id;
        }
      } catch (e) { /* ignore */ }
    },
    // Wszyscy aktywni kierowcy, nie tylko rozliczani gotówką: przez kasę idą też
    // kaucje, zwroty kosztów i wpłaty za najem od kierowców na przelewie.
    async fetchCashDrivers() {
      try {
        const r = await axios.get(process.env.VUE_APP_ROOT_API + 'drivers?take=10000&skip=1&search=', {
          headers: this.authHeaders(),
        });
        const list = r.data.data.result || [];
        this.cashDrivers = list
          .map((d) => ({
            id: d.id,
            label: `${d.firstName || ''} ${d.lastName || ''}`.trim(),
          }))
          .filter((d) => d.label)
          .sort((a, b) => a.label.localeCompare(b.label, 'pl'));
      } catch (e) {
        console.log(e);
      }
    },
    /** Wpis z bazy → wartość pola „Dla kogo / kierowca": kierowca z listy albo sam tekst. */
    odbiorcaZWpisu(item) {
      if (item.driverId) {
        const zListy = this.cashDrivers.find((d) => d.id === item.driverId);
        if (zListy) return zListy;
        // Kierowca spoza listy (np. z innego oddziału) — pokaż nazwisko z wpisu.
        const nazwa = item.driver
          ? `${item.driver.firstName} ${item.driver.lastName}`.trim()
          : item.recipient || '';
        return { id: item.driverId, label: nazwa };
      }
      return item.recipient || null;
    },
    openEntryDialog(item) {
      if (item) {
        this.currentEntryId = item.id;
        this.entry = {
          date: dayjs(item.date).format('YYYY-MM-DD'),
          type: item.type,
          amountZl: item.amount / 100,
          description: item.description,
          odbiorca: this.odbiorcaZWpisu(item),
          operator: item.operator,
          receiptRequired: !!item.receiptRequired,
        };
        this.entrySigned = {
          receiver: !!item.hasSignature,
          worker: !!item.hasWorkerSignature,
        };
      } else {
        this.currentEntryId = null;
        this.entry = this.emptyEntry();
        this.entrySigned = { receiver: false, worker: false };
      }
      this.dialog = true;
      // Podpis z tabletu należy do konkretnego wpisu — nie może przejść na następny.
      this.tabletSignature = null;
      this.tabletError = '';
      this.tabletPairing = null;
      this.stopTabletWaiting();
      this.loadSignatureDevices();
      // Pola podpisu montują się dopiero z otwarciem okna — czyścimy po renderze,
      // żeby nie został podpis z poprzednio edytowanego wpisu.
      this.$nextTick(() => {
        this.clearSignature('receiver');
        this.clearSignature('worker');
      });
    },
    async saveEntry() {
      const payload = {
        date: this.entry.date,
        type: this.entry.type,
        amount: Math.round(this.entry.amountZl * 100),
        description: this.entry.description,
        recipient: this.odbiorcaNazwa || undefined,
        driverId: this.odbiorcaId || undefined,
        operator: this.entry.operator || undefined,
        receiptRequired: this.entry.receiptRequired,
      };
      if (this.entry.receiptRequired) {
        // undefined (a nie null) — backend rozróżnia „brak zmiany" od „wyczyść",
        // więc edycja opisu nie skasuje raz złożonego podpisu.
        // Podpis z tabletu ma pierwszeństwo — pole do rysowania jest wtedy schowane.
        payload.receiptSignature =
          this.tabletSignature || this.grabSignature('receiver') || undefined;
        payload.receiptWorkerSignature = this.grabSignature('worker') || undefined;
      }
      try {
        if (this.currentEntryId) {
          await axios.put(
            process.env.VUE_APP_ROOT_API + 'cash-report/entries/' + this.currentEntryId,
            payload,
            { headers: this.authHeaders() },
          );
        } else {
          await axios.post(
            process.env.VUE_APP_ROOT_API + 'cash-report/entries',
            payload,
            {
              params: { year: this.year, month: this.month, subCompanyId: this.subCompanyId || '' },
              headers: this.authHeaders(),
            },
          );
        }
        this.dialog = false;
        await this.fetchReport();
      } catch (e) {
        console.log(e);
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie zapisano wpisu', 'error');
      }
    },
    async deleteEntry(item) {
      const ok = await Swal.fire({
        title: 'Usunąć wpis?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Tak',
        cancelButtonText: 'Anuluj',
      });
      if (!ok.isConfirmed) return;
      try {
        await axios.delete(
          process.env.VUE_APP_ROOT_API + 'cash-report/entries/' + item.id,
          { headers: this.authHeaders() },
        );
        await this.fetchReport();
      } catch (e) {
        console.log(e);
        Swal.fire('Błąd', 'Nie usunięto wpisu', 'error');
      }
    },
    async downloadReceipt(item) {
      try {
        const r = await axios.get(
          process.env.VUE_APP_ROOT_API + 'cash-report/entries/' + item.id + '/receipt',
          { responseType: 'blob', headers: this.authHeaders() },
        );
        this.downloadBlob(r.data, `pokwitowanie-${item.id}.pdf`);
      } catch (e) {
        console.log(e);
        Swal.fire('Błąd', 'Nie pobrano pokwitowania', 'error');
      }
    },
    async downloadExcel() {
      this.loadingExcel = true;
      try {
        const r = await axios.get(process.env.VUE_APP_ROOT_API + 'cash-report/export/excel', {
          params: { year: this.year, month: this.month, subCompanyId: this.subCompanyId || '' },
          responseType: 'blob',
          headers: this.authHeaders(),
        });
        this.downloadBlob(r.data, `raport-kasowy-${this.year}-${this.month}.xlsx`);
      } catch (e) {
        console.log(e);
        Swal.fire('Błąd', 'Nie pobrano pliku Excel', 'error');
      }
      this.loadingExcel = false;
    },
    async downloadPdf() {
      this.loadingPdf = true;
      try {
        const r = await axios.get(process.env.VUE_APP_ROOT_API + 'cash-report/export/pdf', {
          params: { year: this.year, month: this.month, subCompanyId: this.subCompanyId || '' },
          responseType: 'blob',
          headers: this.authHeaders(),
        });
        this.downloadBlob(r.data, `raport-kasowy-${this.year}-${this.month}.pdf`);
      } catch (e) {
        console.log(e);
        Swal.fire('Błąd', 'Nie pobrano PDF', 'error');
      }
      this.loadingPdf = false;
    },
    async downloadCsv() {
      this.loadingCsv = true;
      try {
        const r = await axios.get(process.env.VUE_APP_ROOT_API + 'cash-report/export/csv', {
          params: { year: this.year, month: this.month, subCompanyId: this.subCompanyId || '' },
          responseType: 'blob',
          headers: this.authHeaders(),
        });
        this.downloadBlob(r.data, `raport-kasowy-${this.year}-${this.month}.csv`);
      } catch (e) {
        console.log(e);
        Swal.fire('Błąd', 'Nie pobrano CSV', 'error');
      }
      this.loadingCsv = false;
    },
    downloadBlob(blob, name) {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.cashreport-page { background: #181C28; min-height: calc(100vh - 56px); padding: 16px 16px 40px; margin: -24px; color: #fff; }
.page-header { padding: 4px 4px 18px; }
.page-eyebrow { font-size: 12px; color: #6E7691; font-weight: 500; }
.page-title { font-size: 22px; font-weight: 800; color: #fff; margin: 2px 0 0; letter-spacing: -0.02em; }
.cashreport-page >>> .v-card { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; box-shadow: none !important; color: #fff !important; }
.cashreport-page >>> .v-card .v-card__title { color: #fff !important; padding: 16px 20px 8px !important; font-weight: 700 !important; font-size: 15px !important; }
.cashreport-page >>> .v-text-field--outlined fieldset, .cashreport-page >>> .v-select--outlined fieldset, .cashreport-page >>> .v-textarea--outlined fieldset { border-color: rgba(255,255,255,0.1) !important; }
.cashreport-page >>> .v-text-field--outlined .v-input__slot, .cashreport-page >>> .v-select--outlined .v-input__slot, .cashreport-page >>> .v-textarea--outlined .v-input__slot { background: #181C28 !important; border-radius: 12px !important; }
.cashreport-page >>> .v-label { color: #6E7691 !important; }
.cashreport-page >>> .v-text-field input, .cashreport-page >>> .v-select__selection, .cashreport-page >>> .v-textarea textarea { color: #fff !important; }
.cashreport-page >>> .v-data-table { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; color: #fff !important; box-shadow: none !important; }
.cashreport-page >>> .v-data-table thead tr th { background: #1F2434 !important; color: #6E7691 !important; font-size: 11px !important; font-weight: 700 !important; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid rgba(255,255,255,0.06) !important; }
.cashreport-page >>> .v-data-table tbody tr { background: #232838 !important; }
.cashreport-page >>> .v-data-table tbody tr:hover { background: #2A3042 !important; }
.cashreport-page >>> .v-data-table tbody tr td { color: #fff !important; border-bottom: 1px solid rgba(255,255,255,0.04) !important; }
.cashreport-page >>> .v-data-footer { background: #1F2434 !important; color: #B0BEC5 !important; border-top: 1px solid rgba(255,255,255,0.06) !important; }
.cashreport-page >>> .v-toolbar { background: transparent !important; color: #fff !important; }
.cashreport-page >>> .v-btn:not(.v-btn--icon) { border-radius: 10px !important; text-transform: none !important; font-weight: 600 !important; }
.cashreport-page >>> .v-chip { border-radius: 999px !important; font-weight: 700 !important; }
.cashreport-page >>> .v-divider { border-color: rgba(255,255,255,0.06) !important; }
.cashreport-page >>> .v-progress-linear { background: rgba(255,255,255,0.06) !important; }
.cashreport-page >>> .v-dialog .v-card { background: #232838 !important; }

/* Pole podpisu — panel jest ciemny, a podpis rysuje się czarnym, więc canvas
   musi mieć własne białe tło. */
.signature-pad,
.signature-pad >>> canvas {
  background: #fff !important;
  border-radius: 8px;
}
.signature-pad {
  border: 1px solid rgba(255, 255, 255, 0.18);
  overflow: hidden;
}

/* Stan kasy — jedna liczba, na którą patrzy biuro, i rozbicie obok. */
.kasa-stan {
  height: 100%;
  padding: 16px 20px;
  border-radius: 10px;
  background: rgba(25, 118, 210, 0.12);
  border: 1px solid rgba(25, 118, 210, 0.35);
}
.kasa-stan__label {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  opacity: 0.75;
}
.kasa-stan__kwota {
  font-size: 34px;
  font-weight: 700;
  line-height: 1.15;
  margin: 2px 0 4px;
}
.kasa-stan__opis { font-size: 12.5px; opacity: 0.7; }
.kasa-przeliczenie { margin-top: 10px; font-size: 12.5px; }
.kasa-przeliczenie__ostatnie { opacity: 0.85; }
.kasa-przeliczenie--ok { color: #4caf50; font-weight: 600; }
.kasa-przeliczenie--roznica { color: #ff9800; font-weight: 600; }
.kasa-rozbicie {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  padding: 12px 16px;
}
.kasa-rozbicie__pozycja {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  opacity: 0.85;
}
.kasa-rozbicie__pozycja--plus strong { color: #4caf50; }
.kasa-rozbicie__pozycja--minus strong { color: #ef5350; }

</style>
